/**
 * ESP + gas-lift virtual views: seven de-prefixed, ancestry-enriched
 * surfaces fronting the per-string ESP/GasLift WF12/WF14 sources.
 *
 * Each source is per-string grain (keyed by the `wellString` producing-string
 * relation), so every view here is a StringContext enriched view keeping
 * identity + the wellString relation (`STRING_KEEP`) and appending the well /
 * field / reservoir ancestry resolved through the FlatHierarchy / Entity
 * registries (`STRING_CONTEXT_ENRICH`) — exactly the StringOutputs pattern.
 *
 * Unlike StringOutputs (whose measures share a single `nmv` source prefix),
 * these sources carry NO uniform de-prefix axis: most columns are already
 * canonical-cased, and only the `EspLimits` / `GasLift*` clusters carry an
 * `esp`/`gl`/`gasLift` prefix that is part of the real column name. So every
 * group resolves with no prefix (`prefix:''`, the default): the shape `suffix`
 * is the verbatim source column, while the agent-facing `canonical` optionally
 * drops the `esp`/`gl` marker for readability. `EspCurveData` exposes only
 * JSON curve file pointers (no scalar/timeseries columns on the source), so its
 * view is identity + wellString + ancestry only — no measure groups.
 *
 * Built per session (not module-level) because the enriched build closes over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import type { DataModelView } from '#core/lib/cognite/view.ts';

import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import type { Shape } from '../shapes/index.ts';
import {
  ESP_CALCULATIONS,
  ESP_LIMITS,
  ESP_MEASUREMENTS,
  GASLIFT_CALCULATIONS,
  GASLIFT_LIMITS,
  GASLIFT_MEASUREMENTS,
  STRING_LIMIT_COMMON,
  STRING_SENSOR_COMMON,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import {
  EspCalculationsStringAgentView,
  EspCurveDataStringAgentView,
  EspLimitsStringAgentView,
  EspMeasurementsStringAgentView,
  GasLiftCalculationsStringAgentView,
  GasLiftLimitsStringAgentView,
  GasLiftMeasurementsStringAgentView,
} from '#schema/generated/ipso/views.ts';

/** StringContext: well/field/reservoir ancestry off the `wellString` ref. */

/** ESP limits = the esp-specific thresholds + the shared rate-limit / risk-flag cluster. */
const ESP_LIMITS_ALL: Shape = [...ESP_LIMITS, ...STRING_LIMIT_COMMON];

/** ESP measurements = the esp-specific pump readings + the shared wellbore / choke sensor cluster. */
const ESP_MEASUREMENTS_ALL: Shape = [...STRING_SENSOR_COMMON, ...ESP_MEASUREMENTS];

/** Gas-lift limits = the gl-specific thresholds + the shared rate-limit / risk-flag cluster. */
const GASLIFT_LIMITS_ALL: Shape = [...GASLIFT_LIMITS, ...STRING_LIMIT_COMMON];

/** Gas-lift measurements = the gl-specific injection readings + the shared wellbore / choke sensor cluster. */
const GASLIFT_MEASUREMENTS_ALL: Shape = [...STRING_SENSOR_COMMON, ...GASLIFT_MEASUREMENTS];

/**
 * One de-prefixed ESP/GasLift surface: a canonical name, agent doc, the source
 * CDF view it fronts, and the de-prefixed groups to keep. All seven share
 * identity + the wellString relation and the well/field/reservoir enrichment.
 */
type EspGasLiftSpec = ViewFamilySpec & { readonly source: DataModelView };

/**
 * The seven ESP/GasLift subviews, declared as data. One de-prefixed view per
 * source; groups resolve with no prefix (`suffix === verbatim source column`).
 */
const ESP_GAS_LIFT_SPECS: readonly EspGasLiftSpec[] = [
  {
    name: 'EspCalculations',
    description: `Model-calculated ESP performance outputs per well string (WF12): calculation status, motor efficiency/load, pump head and average rate${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: EspCalculationsStringAgentView,
    groups: [{ shape: ESP_CALCULATIONS }],
  },
  {
    name: 'EspCurveData',
    description:
      'JSON plot/curve file references per well string from WF12 (gradient/pressure-vs-depth ESP curve outputs). The source carries only file pointers (no scalar/timeseries columns); this view exposes the producing string plus its well/field/reservoir ancestry for scoping.',
    source: EspCurveDataStringAgentView,
    groups: [],
  },
  {
    name: 'EspLimits',
    description: `ESP operating-limit thresholds per well string (WF12): motor temp/current/power, pump delta-T, fluid rates, intake/discharge pressures, head, free-gas tolerance and monitoring flags, plus shared rate limits and flow-assurance risk flags; equipment-protection thresholds, not producer rate limits (ProducerGuidelines owns those)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: EspLimitsStringAgentView,
    groups: [{ shape: ESP_LIMITS_ALL }],
  },
  {
    name: 'EspMeasurements',
    description: `Real-time ESP sensor readings per well string (WF12): pump frequency, intake/discharge pressures and temperatures, motor current/voltage/vibration, plus shared wellbore/choke/flowline readings${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: EspMeasurementsStringAgentView,
    groups: [{ shape: ESP_MEASUREMENTS_ALL }],
  },
  {
    name: 'GasLiftCalculations',
    description: `Model-calculated gas-lift outputs per well string (WF14): GL analysis status, the GLIR variants (selected, measured, network, choke, allocated) and calculated choke size/Cd${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: GasLiftCalculationsStringAgentView,
    groups: [{ shape: GASLIFT_CALCULATIONS }],
  },
  {
    name: 'GasLiftLimits',
    description: `Gas-lift operating-limit thresholds per well string (WF14): casing/wellhead/manifold pressures, BHP target, temperatures and monitoring flags, plus shared rate limits and flow-assurance risk flags; equipment-protection thresholds, not producer rate limits (ProducerGuidelines owns those)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: GasLiftLimitsStringAgentView,
    groups: [{ shape: GASLIFT_LIMITS_ALL }],
  },
  {
    name: 'GasLiftMeasurements',
    description: `Real-time gas-lift sensor readings per well string (WF14): injection rate, CV inlet pressure/size, injection temperature, plus shared wellbore/choke/flowline readings${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: GasLiftMeasurementsStringAgentView,
    groups: [{ shape: GASLIFT_MEASUREMENTS_ALL }],
  },
];

/**
 * Build the seven de-prefixed, ancestry-enriched ESP/GasLift surfaces, each a
 * StringContext view over its own source + the well/field/reservoir enrichment;
 * filter by wellString (or join WellEntity) to scope by hierarchy.
 */
export const useEspGasLiftViews = makeStringContextViews({
  source: (spec: EspGasLiftSpec) => spec.source,
  specs: ESP_GAS_LIFT_SPECS,
});
